import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const EnquiryCard = ({ 
  enquiry, 
  type = 'available', 
  onAccept = () => {}, 
  onDismiss = () => {}, 
  onViewMessages = () => {},
  onEdit = () => {},
  onClose = () => {},
  isLoading = false,
  isOpeningConversation = false,
  conversationError = null,
  isAccepting = false,
  agentBillingStatus = null, // NEW: Pass billing status from parent
  className = '' 
}) => {
  const formatBudget = (min, max, currency) => {
    // Validate currency parameter - must be a valid string
    const validCurrency = typeof currency === 'string' && currency?.length === 3 
      ? currency?.toUpperCase() 
      : 'GBP'; // Default to GBP if invalid
    
    try {
      const formatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: validCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      
      if (min === max) {
        return formatter?.format(min);
      }
      return `${formatter?.format(min)} - ${formatter?.format(max)}`;
    } catch (error) {
      // Fallback formatting if currency is still invalid
      console.error('Currency formatting error:', error);
      return `£${min}${min !== max ? ` - £${max}` : ''}`;
    }
  };

  // ✅ UPDATED: Enhanced date formatting to handle season and month from metadata
  const formatDateRange = (startDate, endDate, metadata) => {
    // Priority 1: Check if specific dates exist (departure_date and return_date)
    if (startDate && endDate) {
      try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        // Validate dates
        if (!isNaN(start?.getTime()) && !isNaN(end?.getTime())) {
          const options = { day: 'numeric', month: 'short' };
          
          if (start?.getFullYear() === end?.getFullYear() && start?.getMonth() === end?.getMonth()) {
            return `${start?.getDate()}-${end?.toLocaleDateString('en-GB', options)}`;
          }
          
          return `${start?.toLocaleDateString('en-GB', options)} - ${end?.toLocaleDateString('en-GB', options)}`;
        }
      } catch (error) {
        console.error('[EnquiryCard] Date formatting error:', error);
      }
    }

    // Priority 2: Check for month selection in metadata
    if (metadata?.dateType === 'month' && metadata?.selectedDate?.value) {
      const { monthName, year } = metadata?.selectedDate?.value;
      if (monthName && year) {
        return `${monthName} ${year} (flexible)`;
      }
    }

    // Priority 3: Check for season selection in metadata
    if (metadata?.dateType === 'season' && metadata?.selectedSeason) {
      const season = metadata?.selectedSeason;
      // Capitalize season name
      const capitalizedSeason = season?.charAt(0)?.toUpperCase() + season?.slice(1)?.toLowerCase();
      return `${capitalizedSeason} (flexible)`;
    }

    // Priority 4: Fallback for any other flexible date scenarios
    if (metadata?.dateType === 'flexible' || (!startDate && !endDate)) {
      return 'Dates flexible';
    }

    // Final fallback
    return 'Date not set';
  };

  const getAccommodationIcon = (standard) => {
    const iconMap = {
      'Budget': 'Home',
      '3*': 'Building',
      '3-star': 'Building',
      '4*': 'Building2',
      '4-star': 'Building2',
      '5*': 'Crown',
      '5-star': 'Crown'
    };
    return iconMap?.[standard] || 'Building';
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = getDaysUntilExpiry(enquiry?.expiryDate);
  const isUrgent = daysLeft <= 2;
  const billingReady = agentBillingStatus?.billing_ready || false;

  return (
    <div className={`bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-all duration-200 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-lg font-semibold text-foreground">{enquiry?.destination}</h3>
            {enquiry?.tripType && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                {enquiry?.tripType}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>{formatDateRange(enquiry?.departure_date, enquiry?.return_date, enquiry?.metadata)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={14} />
              <span>
                {enquiry?.adults || 1} {(enquiry?.adults || 1) === 1 ? 'adult' : 'adults'}
                {enquiry?.children > 0 && `, ${enquiry?.children} ${enquiry?.children === 1 ? 'child' : 'children'}`}
              </span>
            </div>
          </div>
        </div>
        
        {type === 'available' && (
          <div className="flex flex-col items-end">
            {isUrgent && (
              <span className="text-xs text-error font-medium mb-1">
                {daysLeft === 0 ? 'Expires today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
              </span>
            )}
            <StatusIndicator 
              status={enquiry?.status} 
              type="enquiry" 
              size="sm"
            />
          </div>
        )}
        
        {type === 'accepted' && (
          <StatusIndicator 
            status={enquiry?.conversationStatus} 
            type="message" 
            size="sm"
          />
        )}
      </div>

      {/* ✅ ENHANCED: Show adults and children counts separately */}
      <div className="flex items-center space-x-4 mb-3 text-sm text-muted-foreground">
        <div className="flex items-center space-x-1">
          <Icon name="Calendar" size={14} />
          <span>{formatDateRange(enquiry?.departure_date, enquiry?.return_date, enquiry?.metadata)}</span>
        </div>
        <div className="flex items-center space-x-1">
          <Icon name="Users" size={14} />
          <span>
            {enquiry?.adults || 1} {(enquiry?.adults || 1) === 1 ? 'adult' : 'adults'}
            {enquiry?.children > 0 && `, ${enquiry?.children} ${enquiry?.children === 1 ? 'child' : 'children'}`}
          </span>
        </div>
      </div>

      {/* Budget and Accommodation */}
      {(enquiry?.budgetMin || enquiry?.budgetMax || enquiry?.budget_range) && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-1">
            <Icon name="Banknote" size={16} className="text-primary" />
            <span className="font-semibold text-foreground">
              {enquiry?.budget_range 
                ? (() => {
                    try {
                      const budgetObj = typeof enquiry?.budget_range === 'string' 
                        ? JSON.parse(enquiry?.budget_range) 
                        : enquiry?.budget_range;
                      return formatBudget(budgetObj?.min, budgetObj?.max, budgetObj?.currency);
                    } catch {
                      return enquiry?.budget_range;
                    }
                  })()
                : formatBudget(enquiry?.budgetMin, enquiry?.budgetMax, enquiry?.currency)
              }
            </span>
          </div>
          
          {enquiry?.accommodation_preferences && (
            <div className="flex items-center space-x-1">
              <Icon name={getAccommodationIcon(enquiry?.accommodation_preferences)} size={16} className="text-primary" />
              <span className="text-sm text-muted-foreground">{enquiry?.accommodation_preferences}</span>
            </div>
          )}
        </div>
      )}

      {/* ✅ NEW: Trip Description Section */}
      {enquiry?.description && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-start space-x-2 mb-1">
            <Icon name="FileText" size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Trip Details</p>
          </div>
          <p className="text-sm text-foreground pl-5 line-clamp-3">{enquiry?.description}</p>
        </div>
      )}

      {/* Customer Preferences */}
      {enquiry?.preferences && enquiry?.preferences?.length > 0 && (
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-2">Customer preferences:</p>
          <div className="flex flex-wrap gap-1">
            {enquiry?.preferences?.slice(0, 3)?.map((preference, index) => (
              <span 
                key={index}
                className="text-xs bg-accent/10 text-accent-foreground px-2 py-1 rounded-full"
              >
                {preference}
              </span>
            ))}
            {enquiry?.preferences?.length > 3 && (
              <span className="text-xs text-muted-foreground px-2 py-1">
                +{enquiry?.preferences?.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* ✅ ENHANCED: Additional Requirements with better mapping */}
      {enquiry?.additional_requirements && (
        <div className="mb-4">
          <div className="flex items-center space-x-1 mb-2">
            <Icon name="Info" size={14} className="text-primary" />
            <p className="text-sm font-medium text-primary">Additional Requirements:</p>
          </div>
          <p className="text-sm text-foreground pl-5 line-clamp-2">{enquiry?.additional_requirements}</p>
        </div>
      )}

      {/* ✅ NEW: Accommodation Preferences Detail (if not already shown in header) */}
      {enquiry?.accommodation_preferences && type === 'proposed' && (
        <div className="mb-4 flex items-center space-x-2 p-2 bg-primary/5 rounded-lg">
          <Icon name="Home" size={14} className="text-primary" />
          <span className="text-sm text-foreground">
            <span className="font-medium">Accommodation: </span>
            {enquiry?.accommodation_preferences}
          </span>
        </div>
      )}

      {/* Special Requirements */}
      {enquiry?.specialRequirements && (
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-1">Special requirements:</p>
          <p className="text-sm text-foreground line-clamp-2">{enquiry?.specialRequirements}</p>
        </div>
      )}

      {/* ✅ NEW: More Details Section - Only show if populated */}
      {enquiry?.moreDetails && (
        <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start space-x-2 mb-2">
            <Icon name="FileText" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm font-medium text-primary">More Details from Customer:</p>
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap pl-6">{enquiry?.moreDetails}</p>
        </div>
      )}

      {/* Customer Info for Accepted Enquiries */}
      {type === 'accepted' && enquiry?.customer && (
        <div className="flex items-center space-x-3 mb-4 p-3 bg-muted/50 rounded-lg">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-primary-foreground">
              {enquiry?.customer?.name?.charAt(0)}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">{enquiry?.customer?.name}</p>
            <p className="text-xs text-muted-foreground">
              Joined {new Date(enquiry.customer.joinedDate)?.toLocaleDateString('en-GB')}
            </p>
          </div>
          {enquiry?.unreadMessages > 0 && (
            <div className="ml-auto">
              <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-full">
                {enquiry?.unreadMessages} new
              </span>
            </div>
          )}
        </div>
      )}

      {/* ✅ NEW: Quote Status Display for Accepted Enquiries */}
      {type === 'accepted' && enquiry?.conversation?.[0] && (
        <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon 
                name={enquiry?.conversation?.[0]?.quote_price ? "CheckCircle" : "Clock"} 
                size={16} 
                className={enquiry?.conversation?.[0]?.quote_price ? "text-success" : "text-muted-foreground"} 
              />
              <span className="text-sm font-medium text-foreground">
                {enquiry?.conversation?.[0]?.quote_price ? "Quote sent" : "No quote yet"}
              </span>
            </div>
            {enquiry?.conversation?.[0]?.quote_price && (
              <div className="text-right">
                <p className="text-lg font-bold text-primary">
                  {(() => {
                    const currency = enquiry?.conversation?.[0]?.quote_currency || 'GBP';
                    const price = enquiry?.conversation?.[0]?.quote_price;
                    try {
                      const formatter = new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: currency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      });
                      return formatter?.format(price);
                    } catch (error) {
                      return `£${price}`;
                    }
                  })()}
                </p>
                {enquiry?.conversation?.[0]?.quote_submitted_at && (
                  <p className="text-xs text-muted-foreground">
                    Sent {new Date(enquiry?.conversation?.[0]?.quote_submitted_at)?.toLocaleDateString('en-GB')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 pt-4 border-t border-border">
        {type === 'available' && (
          <div className="space-y-3">
            {/* Billing Warning */}
            {!billingReady && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-yellow-900 font-medium">Payment method required</p>
                  <p className="text-xs text-yellow-800 mt-0.5">
                    Add a payment method in Settings to quote on enquiries.
                  </p>
                </div>
              </div>
            )}

            {/* Payment Failed Warning */}
            {agentBillingStatus?.billing_status === 'payment_failed' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-red-900 font-medium">Last payment failed</p>
                  <p className="text-xs text-red-800 mt-0.5">
                    {agentBillingStatus?.last_payment_error || 'Please update your payment method in Settings.'}
                  </p>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                onClick={onAccept}
                disabled={isAccepting || !billingReady}
                className="flex-1"
                iconName={isAccepting ? "Loader2" : "FileText"}
                iconClassName={isAccepting ? "animate-spin" : ""}
                iconPosition="left"
              >
                {isAccepting ? 'Processing...' : billingReady ? 'Pay £7.00 & Quote on This' : 'Add Payment Method'}
              </Button>
              <Button
                onClick={onDismiss}
                variant="outline"
                disabled={isAccepting}
                iconName="X"
                iconPosition="left"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {type === 'accepted' && (
          <>
            <Button
              onClick={() => onViewMessages?.(enquiry)}
              variant="primary"
              size="sm"
              disabled={isOpeningConversation}
              className="flex items-center gap-2"
            >
              <Icon name="MessageSquare" size={16} />
              {isOpeningConversation ? 'Opening...' : 'Read'}
            </Button>
            {conversationError && (
              <p className="text-xs text-red-600 mt-1">
                {conversationError}
              </p>
            )}
          </>
        )}
      </div>

      {/* Urgency Indicator */}
      {type === 'available' && isUrgent && (
        <div className="mt-3 p-2 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={14} className="text-error" />
            <span className="text-xs text-error font-medium">
              Urgent: This enquiry expires soon!
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryCard;